import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Download, Bell, Zap, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from '../lib/axios';
import { API } from '../App';

const InstallAppPopup = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(true);
  const [comingSoonActive, setComingSoonActive] = useState(false);

  // Pages where popup should NOT appear
  const excludedPaths = ['/login', '/register', '/auth', '/forgot-password', '/reset-password', '/admin'];
  const isExcludedPage = excludedPaths.some(path => location.pathname.startsWith(path));

  const translations = {
    en: {
      title: "Get the App",
      subtitle: "Notifications & faster access",
      benefit1: "Notifications",
      benefit2: "Faster",
      cta: "Learn How",
      dismiss: "Later",
    },
    nl: {
      title: "Download de App",
      subtitle: "Notificaties & snellere toegang",
      benefit1: "Notificaties",
      benefit2: "Sneller",
      cta: "Bekijk Hoe",
      dismiss: "Later",
    },
    es: {
      title: "Obtén la App",
      subtitle: "Notificaciones y acceso rápido",
      benefit1: "Notificaciones",
      benefit2: "Más rápido",
      cta: "Ver Cómo",
      dismiss: "Después",
    }
  };

  const txt = translations[language] || translations.en;

  // Check if Coming Soon mode is active
  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        setComingSoonActive(response.data?.enabled === true);
      } catch (error) {
        setComingSoonActive(false);
      }
    };
    checkComingSoon();
  }, []);

  useEffect(() => {
    // Don't show if Coming Soon mode is active
    if (comingSoonActive) return;
    
    // Don't show on homepage (could be Coming Soon page)
    if (location.pathname === '/') return;
    
    // Check if already installed as PWA
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(isInStandaloneMode);

    // Don't show if already installed
    if (isInStandaloneMode) return;

    // Check if user dismissed the popup recently (within 7 days)
    const dismissedAt = localStorage.getItem('installPopupDismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }

    // Check if user has visited at least 3 times
    const visitCount = parseInt(localStorage.getItem('visitCount') || '0') + 1;
    localStorage.setItem('visitCount', visitCount.toString());

    // Show popup after 3 visits and a short delay
    if (visitCount >= 3) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [comingSoonActive, location.pathname]);

  const handleDismiss = () => {
    localStorage.setItem('installPopupDismissed', Date.now().toString());
    setIsVisible(false);
  };

  const handleCtaClick = () => {
    // Don't set dismissed, user is interested
    setIsVisible(false);
  };

  // Don't render if installed, not visible, or on excluded pages
  if (isStandalone || !isVisible || isExcludedPage) return null;

  return (
    <div 
      className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 z-[100] animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="install-app-banner"
    >
      <div className="w-full sm:w-auto sm:min-w-[340px] bg-card/95 backdrop-blur-md border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-3 sm:p-4">
          {/* Header row */}
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-primary to-purple-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Download className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            
            {/* Text content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base leading-tight">{txt.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-tight mt-0.5">{txt.subtitle}</p>
                </div>
                
                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-full transition-colors flex-shrink-0 -mt-0.5 -mr-1"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {/* Benefits - inline on mobile */}
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Bell className="w-3.5 h-3.5 text-blue-500" />
                  <span>{txt.benefit1}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                  <span>{txt.benefit2}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions row */}
          <div className="flex items-center gap-2 mt-3">
            <Link
              to="/install-app"
              onClick={handleCtaClick}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              {txt.cta}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <button
              onClick={handleDismiss}
              className="px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-xl transition-colors text-sm"
            >
              {txt.dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPopup;
